import { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community';
import { t } from 'i18next';
import {
    hrPositionMappingValueGetter,
    recurrentFormatter,
} from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/gridHelpers';
import { agentPayComponentHypothesisValueGetter } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/PayComponent/helpers/dataHelpers';
import {
    DetailedAgentPayComponentHypothesis,
    SimulationAgentPayComponentData,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/PayComponent/helpers/types';
import { envelopePayComponentHypothesisValueGetter } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/PayComponents/helpers/gridHelpers';
import { SimulationPayComponentsData } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/PayComponents/helpers/definitions';
import {
    PAY_COMPONENT,
    START_DATE,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import {
    isRowGrouped,
    isRowPinned,
} from 'routes/HumanResourcesModule/Simulation/helpers/gridHelpers';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import {
    JOB_LABEL,
    MANTY_STATUS,
    ORG_CHART_HR_MAPPING,
    ORG_CHART_HR_NAME,
    RECURRENCE,
} from 'routes/HumanResourcesModule/business/definitions';
import { getAgentColumn } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/agentColumnHelpers';

import { TEXT_FILTER } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/defaultColDefs';
import { formatDate } from 'helpers/dateHelper';

const envelopePayComponentHypothesisCellRendererSelector = (
    params: ICellRendererParams<SimulationPayComponentsData>,
) => {
    if (isRowGrouped(params)) return { component: 'agGroupCellRenderer' };
    if (isRowPinned(params)) return { component: undefined };

    return {
        component: 'editEnvelopePayComponentHypothesisCellRenderer',
    };
};

const agentPayComponentHypothesisCellRendererSelector = (
    params: ICellRendererParams<SimulationAgentPayComponentData>,
) => {
    if (isRowGrouped(params)) return { component: 'agGroupCellRenderer' };
    if (isRowPinned(params)) return { component: undefined };

    return {
        component: 'agentHypothesisCellRenderer',
    };
};
export const getColDefs = ({
    hrSimulation,
}: {
    hrSimulation: HrSimulation;
}): (ColDef | ColGroupDef)[] => [
    {
        ...TEXT_FILTER,
        headerName: t(`humanResources.simulation.${ORG_CHART_HR_MAPPING}`),
        field: ORG_CHART_HR_NAME,
        enableRowGroup: true,
    },
    {
        ...TEXT_FILTER,
        headerName: t('humanResources.simulation.payComponent'),
        field: PAY_COMPONENT,
        enableRowGroup: false,
        suppressColumnsToolPanel: true,
    },
    {
        ...TEXT_FILTER,
        headerName: t('humanResources.simulation.hypothesis'),
        cellRendererSelector:
            envelopePayComponentHypothesisCellRendererSelector,
        valueGetter: envelopePayComponentHypothesisValueGetter,
        cellRendererParams: {
            hrSimulation,
            isUpdateEnabled: false,
        },
        filterValueGetter: () => {
            return t('humanResources.simulation.editHypothesis');
        },
        enableRowGroup: false,
    },
];

export const getAgentPayComponentColDefs = ({
    hrSimulation,
    agentPayComponentHypotheses,
}: {
    hrSimulation: HrSimulation;
    agentPayComponentHypotheses: DetailedAgentPayComponentHypothesis[];
}): (ColDef | ColGroupDef)[] => [
    getAgentColumn(),
    {
        ...TEXT_FILTER,
        headerName: t(`humanResources.simulation.${ORG_CHART_HR_MAPPING}`),
        field: ORG_CHART_HR_NAME,
        enableRowGroup: false,
    },
    {
        ...TEXT_FILTER,
        headerName: t('humanResources.simulation.payComponent'),
        field: PAY_COMPONENT,
        enableRowGroup: false,
        suppressColumnsToolPanel: true,
        resizable: true,
    },
    {
        ...TEXT_FILTER,
        headerName: t(`humanResources.simulation.${JOB_LABEL}`),
        field: JOB_LABEL,
        resizable: true,
    },
    {
        ...TEXT_FILTER,
        headerName: t('humanResources.settings.recurrence'),
        field: RECURRENCE,
        enableRowGroup: false,
        suppressColumnsToolPanel: true,
        valueFormatter: recurrentFormatter,
        filterValueGetter: recurrentFormatter,
    },
    {
        ...TEXT_FILTER,
        headerName: t('humanResources.settings.mantyStatus'),
        field: MANTY_STATUS,
        enableRowGroup: false,
        suppressColumnsToolPanel: true,
        filterValueGetter: hrPositionMappingValueGetter(MANTY_STATUS),
        valueGetter: hrPositionMappingValueGetter(MANTY_STATUS),
    },
    {
        ...TEXT_FILTER,
        headerName: t('humanResources.simulation.hypothesis'),
        cellRendererSelector: agentPayComponentHypothesisCellRendererSelector,
        valueGetter: agentPayComponentHypothesisValueGetter(
            agentPayComponentHypotheses,
        ),
        filterValueGetter: (params) => {
            return `${params.data[PAY_COMPONENT]} ${formatDate(new Date(params.data[START_DATE]))}`;
        },
        cellRendererParams: {
            hrSimulation,
            isUpdateEnabled: false,
        },
        enableRowGroup: false,
    },
];
